import { useEffect, useMemo, useRef, useState } from "react";
import Input from "components/forms/input";
import { toast } from "react-hot-toast";
import { useAuth } from "contexts/auth-context";
import { useResultSet } from "repositories/db-repository";

function AsyncPOHeaderPicker({
  disabled,
  value,                 // selected PO id
  onChange,              // (id, row) => void
  initialLabel,
  supplier_id,
  warehouse_id,          // optional hints (not used by server unless you wired it)
  outlet_id,
}) {
  const { user } = useAuth();
  const baseURL = process.env.REACT_APP_REMAX_PS_API_URL;

  // fetch once via your existing RPC wrapper
  const { data: poRes, isLoading } = useResultSet(
    baseURL,
    "purchase_header",
    {
      // your PHP only supports parent_id today; these are for future server-side filters
      supplier_id,
      outlet_id,
      limit: 15,
      flow_detail_id: "SALES_ORDER_COMPLETED", // only if your SQL supports it
    }
  );

  const allRows = Array.isArray(poRes?.purchase_header)
    ? poRes.purchase_header
    : Array.isArray(poRes?.data)
    ? poRes.data
    : Array.isArray(poRes)
    ? poRes
    : [];

  const [label, setLabel] = useState(initialLabel || "");
  const [query, setQuery] = useState("");
  const debounceRef = useRef();

  // client-side search (fast + avoids laggy big dropdowns)
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return allRows;
    return allRows.filter(r =>
      String(r.purchase_transaction_id || "").toLowerCase().includes(q) ||
      String(r.supplier_name || "").toLowerCase().includes(q)
    );
  }, [allRows, query]);

  // hydrate label when editing
  useEffect(() => {
    if (!value) return;
    const found = allRows.find(r => r.id === value);
    if (found) setLabel(`${found.id} • ${found.supplier_name ?? "-"}`);
  }, [value, allRows]);

  if (disabled) return <Input disabled value={label || "-"} id="po_label" />;

  if (!baseURL) {
    toast.error("REMAX_PS_API_URL belum diset");
    return null;
  }

  return (
    <div className="space-y-2">
      <input
        className="w-full px-3 py-2 text-sm border rounded-xl"
        placeholder="Cari No. PO / Supplier…"
        value={query}
        onChange={(e) => {
          clearTimeout(debounceRef.current);
          const v = e.target.value;
          debounceRef.current = setTimeout(() => setQuery(v), 150);
        }}
      />
      <div className="h-72 overflow-auto border rounded-xl">
        {isLoading && <div className="p-3 text-xs text-neutral-500">Memuat…</div>}
        {!isLoading && filtered.length === 0 && (
          <div className="p-3 text-xs text-neutral-500">Tidak ada hasil</div>
        )}
        {!isLoading && filtered.map(row => (
          <button
            type="button"
            key={row.id}
            className={`w-full text-left px-3 py-2 text-xs hover:bg-neutral-50 ${value === row.id ? "bg-neutral-100" : ""}`}
            onClick={() => {
              setLabel(`${row.id} • ${row.supplier_name ?? "-"}`);
              onChange(row.id, row);
            }}
          >
            <div className="font-medium">{row.purchase_transaction_id}</div>
            <div className="text-neutral-500">
              {row.supplier_name ?? "-"} • {row.warehouse_name ?? "-"}
            </div>
            <div className="text-[10px] text-neutral-500">
              Status: {row.flow_detail_id ?? "-"}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

export default AsyncPOHeaderPicker;
